import { useEffect, useState } from "react";
import { Dialog } from "../../../components/ui/dialog";
import CommissionCard from "./CommissionCard";
import NewSimulationCard from "./NewSimulationCard";
import ChooseSimulationModal from "./ChooseSimulationModal";
import FgtsModal from "./Simulations/Fgts";
import CrefazModal from "./Simulations/Crefaz";
import { PartnerType } from "@/app/utils/partners.enum";
import SalvcarModal from "./Simulations/Salvcar";
import PanModal from "./Simulations/Pan";
import CltModal from "./Simulations/Clt";
import CrefisaModal from "./Simulations/Crefisa";
import CreateLinkModal from "../../Links/components/CreateLinkModal";
import { selfHireService } from "@/app/services/selfHireService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/view/components/ui/use-toast";
import QRCodeModal from "../../Links/components/QRCodeModal";
import AlertModal from "./AlertModal";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";
import C6BankModal from "./Simulations/C6Bank";
import { useLocation, useNavigate } from "react-router-dom";

type StepType = PartnerType | "chooseSimulation" | null;

export default function SellerCards({ value = 0 }: { value?: number }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<StepType>(null);
  const [pendingPartner, setPendingPartner] = useState<PartnerType | null>(
    null,
  );
  const [isCreateLinkModalOpen, setIsCreateLinkModalOpen] = useState(false);
  const [newLinkName, setNewLinkName] = useState("");
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<{ name: string }>({
    name: "",
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.openChooseSimulation) {
      setOpen(true);
      setStep("chooseSimulation");

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const handleOpen = () => {
    setOpen(true);
    setStep("chooseSimulation");
  };

  const handleOptionSelect = (option: PartnerType) => {
    const alert = catalog?.find((item) => item.key === option)?.alert;
    if (alert?.active) {
      setPendingPartner(option);
      setIsAlertOpen(true);
      return;
    }
    setStep(option);
  };

  const handleBackToChoose = () => setStep("chooseSimulation");

  const { catalog, isLoading } = useProductsCatalog();

  const { mutate: createLinkMutation, isPending } = useMutation({
    mutationFn: async ({
      name,
      tables,
    }: {
      name: string;
      tables: string[];
    }) => {
      return await selfHireService.createLink({ name, tables });
    },
    onSuccess: (variables) => {
      queryClient.invalidateQueries({ queryKey: ["AllUserLinks"] });

      setIsCreateLinkModalOpen(false);

      setNewLinkName("");
      setSelectedTables([]);

      setSelectedLink({ name: variables.name });
      setIsModalOpen(true);

      toast({
        variant: "default",
        title: "Link criado com sucesso!",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title:
          error?.message || "Não foi possível criar o link, tente novamente",
      });
    },
  });

  const handleCreateLink = () => {
    if (newLinkName && selectedTables) {
      createLinkMutation({
        name: newLinkName.trim(),
        tables: selectedTables,
      });
    }
  };

  const handleOpenLinkModal = () => {
    setOpen(false);
    setIsCreateLinkModalOpen(true);
  };

  const currentAlert = pendingPartner
    ? catalog?.find((item) => item.key === pendingPartner)?.alert
    : undefined;

  const handleAlertOpenChange = (nextOpen: boolean) => {
    setIsAlertOpen(nextOpen);
    if (nextOpen) return;

    const partner = pendingPartner;
    setPendingPartner(null);

    if (currentAlert?.block) {
      setStep("chooseSimulation");
      return;
    }

    if (partner) setStep(partner);
  };

  return (
    <>
      <CommissionCard value={value} />

      <Dialog open={open} onOpenChange={setOpen}>
        <NewSimulationCard onClick={handleOpen} />

        {step === "chooseSimulation" && (
          <ChooseSimulationModal
            handleOpenLinkModal={handleOpenLinkModal}
            onOptionSelect={handleOptionSelect}
            catalog={catalog}
            isLoading={isLoading}
          />
        )}

        {step === PartnerType.FGTS && <FgtsModal onBack={handleBackToChoose} />}

        {step === PartnerType.CREFAZ && (
          <CrefazModal
            onBack={handleBackToChoose}
            onClose={() => setOpen(false)}
          />
        )}

        {step === PartnerType.SALVCAR && (
          <SalvcarModal onBack={handleBackToChoose} />
        )}

        {step === PartnerType.PAN && <PanModal onBack={handleBackToChoose} />}

        {step === PartnerType.CLT && <CltModal onBack={handleBackToChoose} />}

        {step === PartnerType.C6 && <C6BankModal onBack={handleBackToChoose} />}
        {step === PartnerType.CREFISA && (
          <CrefisaModal
            onBack={handleBackToChoose}
            onClose={() => setOpen(false)}
            partnerType={PartnerType.CREFISA}
          />
        )}

        {step === PartnerType.CREFISA_INSS && (
          <CrefisaModal
            onBack={handleBackToChoose}
            onClose={() => setOpen(false)}
            partnerType={PartnerType.CREFISA_INSS}
          />
        )}
      </Dialog>

      <AlertModal
        isOpen={isAlertOpen}
        onOpenChange={handleAlertOpenChange}
        alert={currentAlert}
      />

      <CreateLinkModal
        visible={isCreateLinkModalOpen}
        newLinkName={newLinkName}
        setNewLinkName={setNewLinkName}
        selectedTables={selectedTables}
        setSelectedTables={setSelectedTables}
        onCreateLink={handleCreateLink}
        isLoading={isPending}
        onClose={() => setIsCreateLinkModalOpen(false)}
      />

      <QRCodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        linkName={selectedLink.name}
      />
    </>
  );
}
